import { useCoreServices } from '@bangle.io/context';
import { writeLocalUnsavedDrafts } from '@bangle.io/service-core';
import React, { useEffect } from 'react';

type SaveStatusSource = {
  getUnsavedBodies: () => ReadonlyMap<string, string>;
  hasPendingOrFailedSave: () => boolean;
  subscribeToSaveStatus: (listener: () => void) => () => void;
  flushDebouncedSaves?: () => void;
};

type UnsavedChangesTarget = {
  setUnsavedChanges: (hasUnsavedChanges: boolean) => void;
};

export function connectSaveProtection(
  saveStatus: SaveStatusSource,
  navigation: UnsavedChangesTarget,
): () => void {
  const syncProtection = () => {
    navigation.setUnsavedChanges(saveStatus.hasPendingOrFailedSave());
  };

  syncProtection();
  const unsubscribe = saveStatus.subscribeToSaveStatus(syncProtection);

  const flushLocalBackup = () => {
    try {
      // Kick debounce-held writes so they are in-flight before the tab dies;
      // drafts below still cover the case where the write cannot finish.
      saveStatus.flushDebouncedSaves?.();
      writeLocalUnsavedDrafts(saveStatus.getUnsavedBodies());
    } catch {
      // pagehide/beforeunload must never throw; losing the sync backup is
      // preferable to aborting unload or masking IndexedDB drafts.
    }
  };

  const target =
    typeof globalThis !== 'undefined' && 'addEventListener' in globalThis
      ? globalThis
      : undefined;

  // pagehide covers tab discard / mobile freeze better than beforeunload alone.
  target?.addEventListener('pagehide', flushLocalBackup);
  target?.addEventListener('beforeunload', flushLocalBackup);

  return () => {
    unsubscribe();
    target?.removeEventListener('pagehide', flushLocalBackup);
    target?.removeEventListener('beforeunload', flushLocalBackup);
    navigation.setUnsavedChanges(false);
  };
}

export function SaveProtection() {
  const { navigation, editorEngine } = useCoreServices();

  useEffect(
    () => connectSaveProtection(editorEngine, navigation),
    [navigation, editorEngine],
  );

  return null;
}
